import type { CalibrationTier, EvidenceAxisToken, MandateTier } from './evidence.js';

// ── Blueprint catalog ────────────────────────────────────────────────────────
// The named acquisition patterns. Each is a THIN reading of evidence axes under
// a hard eligibility gate — no new scoring (invariant 1). `mandateTier` says how
// much buyer context it needs to mean anything (invariant 2); `calibrationTier`
// starts `structural` (grey) and is promoted to `deal-validated` (blue) only when
// the deal feed corroborates it (Phase 6). All fit is computed in-build against a
// mandate — nothing is precomputed universe-wide (invariant 6).

export interface Blueprint {
  /** Stable machine token. Frozen once shipped. */
  token: string;
  label: string;
  /** One-line thesis this pattern asserts. */
  summary: string;
  mandateTier: MandateTier;
  calibrationTier: CalibrationTier;
  /** Human-readable HARD gate — an entity that fails it is never labelled. */
  gate: string;
  /** Evidence axes the fit reads. */
  axes: EvidenceAxisToken[];
}

export const BLUEPRINTS = [
  {
    token: 'platform_anchor',
    label: 'Platform anchor',
    summary: 'A scaled, well-run base to build a buy-and-build around.',
    mandateTier: 'market-relational',
    calibrationTier: 'structural',
    gate: 'Above the buyer size band; quality tier not weak; management/scale to anchor on.',
    axes: [
      'revenue_abs',
      'quality_tier',
      'quality_direction',
      'ownership_type',
      'market_fragmentation',
    ],
  },
  {
    token: 'bolt_on',
    label: 'Bolt-on',
    summary: 'A small adjacent add that plugs into the buyer’s existing platform.',
    mandateTier: 'buyer-relational',
    calibrationTier: 'structural',
    gate: 'Within reach of a buyer anchor; below anchor size; adjacent subsegment/geography.',
    axes: ['adjacency_to_anchor', 'catchment_overlap', 'revenue_abs', 'specialism_fit'],
  },
  {
    token: 'buy_and_build_entry',
    label: 'Buy-and-build entry',
    summary: 'A first platform in a fragmented white-space cell.',
    mandateTier: 'market-relational',
    calibrationTier: 'structural',
    gate: 'Fragmented cell (low CR5) with white space; entity large enough to seed a platform.',
    axes: ['market_fragmentation', 'white_space', 'revenue_abs', 'opportunity_index'],
  },
  {
    token: 'consolidation_play',
    label: 'Consolidation play',
    summary: 'Roll up a fragmented local cell around an anchor.',
    mandateTier: 'market-relational',
    calibrationTier: 'structural',
    gate: 'Fragmented cell with multiple reachable independents; consolidation benchmark supports it.',
    axes: ['market_fragmentation', 'white_space', 'catchment_overlap', 'opportunity_index'],
  },
  {
    token: 'succession_sale',
    label: 'Succession / retirement sale',
    summary: 'An owner-operator likely to exit — high seller readiness.',
    mandateTier: 'intrinsic',
    calibrationTier: 'structural',
    gate: 'Independent ownership; high seller-readiness signals (owner age / tenure / no successor).',
    axes: ['seller_readiness', 'ownership_type', 'origination_signal'],
  },
  {
    token: 'distress_turnaround',
    label: 'Distress / turnaround',
    summary: 'A weak or deteriorating operator available below intrinsic value.',
    mandateTier: 'intrinsic',
    calibrationTier: 'structural',
    gate: 'Weak/deteriorating quality OR overdue accounts; screened against the value-trap red-flag.',
    axes: ['quality_tier', 'quality_direction', 'accounts_overdue', 'outstanding_charges'],
  },
  {
    token: 'carve_out',
    label: 'Carve-out',
    summary: 'A separable opco or brand within a larger group.',
    mandateTier: 'intrinsic',
    calibrationTier: 'structural',
    gate: 'Member of an economic group; separable footprint; not the group’s core.',
    axes: ['economic_group', 'revenue_abs', 'specialism_fit', 'ownership_type'],
  },
  {
    token: 'specialism_scarcity',
    label: 'Specialism scarcity',
    summary: 'A specialism under-supplied versus demand in its own catchment — the X-factor.',
    mandateTier: 'market-relational',
    calibrationTier: 'structural',
    gate: 'Carries a mandate-relevant specialism; catchment demand exceeds local specialism supply.',
    axes: ['specialism_fit', 'specialism_scarcity', 'white_space', 'revenue_abs'],
  },
  {
    token: 'quality_arbitrage',
    label: 'Quality arbitrage',
    summary: 'A strong operator that looks under-recognised or improvable.',
    mandateTier: 'intrinsic',
    calibrationTier: 'structural',
    gate: 'Strong/improving quality with modest signal — value not yet priced in.',
    axes: ['quality_tier', 'quality_direction', 'origination_signal', 'margin_abs'],
  },
  {
    token: 'footprint_gap_filler',
    label: 'Footprint gap-filler',
    summary: 'Fills a geographic gap in the buyer’s own network.',
    mandateTier: 'buyer-relational',
    calibrationTier: 'structural',
    gate: 'Sits in a cell where the buyer has no presence; subsegment matches the mandate.',
    axes: ['footprint_gap', 'specialism_fit', 'revenue_abs', 'white_space'],
  },
] as const satisfies ReadonlyArray<Blueprint>;

export type BlueprintToken = (typeof BLUEPRINTS)[number]['token'];

// ── Red flags (disqualifiers, not blueprints) ────────────────────────────────
// The two-sided thesis (invariant 3): every build screens matches against these.

export interface RedFlag {
  token: string;
  label: string;
  summary: string;
  axes: EvidenceAxisToken[];
}

export const RED_FLAGS = [
  {
    token: 'value_trap',
    label: 'Value trap',
    summary: 'Cheap for a reason — distress with secured debt and deteriorating quality.',
    axes: ['outstanding_charges', 'accounts_overdue', 'quality_direction', 'margin_abs'],
  },
  {
    token: 'saturated_rollup',
    label: 'Saturated roll-up',
    summary: 'The cell is already consolidated — thin remaining runway.',
    axes: ['market_fragmentation', 'white_space', 'opportunity_index'],
  },
  {
    token: 'cannibalisation',
    label: 'Cannibalisation',
    summary: 'Catchment overlaps the buyer’s existing sites — adds cost, not reach.',
    axes: ['catchment_overlap', 'footprint_gap'],
  },
  {
    token: 'over_concentration',
    label: 'Over-concentration',
    summary: 'Buying it tips the buyer past competition-authority thresholds in the cell.',
    axes: ['market_fragmentation', 'catchment_overlap'],
  },
] as const satisfies ReadonlyArray<RedFlag>;

export type RedFlagToken = (typeof RED_FLAGS)[number]['token'];
